import { useEffect } from 'react';
import noop, { NoOp } from 'utils/noop';
import { BaseOption, TypeaheadSelect, SelectionDetails } from 'ui/selects';
import ResourceTooltipWrapper from '../ResourceSelect/ResourceTooltipWrapper';
import { InternalOnChangeProp, ResourceFieldProps } from './ResourceTypeaheadSelect.types';

interface ResourceTypeaheadSelectOption extends BaseOption {
  label: string;
}

interface ResourceTypeaheadSelectProps extends ResourceFieldProps {
  /** Are the options currently loading? */
  isLoading?: boolean;
  /** Options for the dropdown */
  options?: ResourceTypeaheadSelectOption[];
  /** tooltip text shown when no resource options are available */
  noResourceHelpText?: string;
  onChange: (e: InternalOnChangeProp) => void | NoOp;
  /** setting tooltip content if disabled */
  setTooltipContent?: (content: string) => void;
}

/**
 * Returns a select field configured for resources
 */
export const ResourceTypeaheadSelect = ({
  isLoading = false,
  noResourceHelpText = '',
  options = [],
  value,
  onChange = noop,
  setTooltipContent = noop,
  ...props
}: ResourceTypeaheadSelectProps) => {
  useEffect(() => {
    if (value) {
      // since custom event can be a user input field outside of options, set tooltip to value
      setTooltipContent(value);
    }
  }, [value]);

  return (
    <ResourceTooltipWrapper
      hasTooltip={!options.length && !value}
      tooltipText={isLoading ? 'Loading...' : noResourceHelpText}
    >
      <TypeaheadSelect
        {...props}
        data-formfieldtype="select"
        options={options.sort((a, b) => a.label.localeCompare(b.label))}
        onSelect={(valArr: string[], { selectedOptions = [] }: SelectionDetails) => {
          if (selectedOptions.length) onChange(selectedOptions[0] as InternalOnChangeProp);
        }}
        defaultValues={value ? [value] : []}
        isFullWidth
      />
    </ResourceTooltipWrapper>
  );
};

export default ResourceTypeaheadSelect;
